import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiCall } from '../lib/api';
import { cn } from '../lib/utils';
import { RepeatCadenceEditor, RepeatCadenceValue } from '../components/RepeatCadenceEditor';
import {
  ImageDisplayEditor,
  ImageDisplayValue,
  deserializeImageDisplay,
  serializeImageDisplayForPartner,
} from '../components/ImageDisplayEditor';
import { HeartbeatsEditor } from '../components/HeartbeatsEditor';

type AuthType = 'none' | 'basic' | 'bearer' | 'api_key_header' | 'api_key_query';

const AUTH_TYPE_OPTIONS: Array<{ value: AuthType; label: string; hint: string }> = [
  { value: 'basic', label: 'Basic Auth', hint: 'HTTP Basic — Authorization: Basic base64(user:pass)' },
  { value: 'bearer', label: 'Bearer Token', hint: 'Authorization: Bearer <token>' },
  { value: 'api_key_header', label: 'API Key (header)', hint: 'Custom header (e.g. X-API-Key: <token>)' },
  { value: 'api_key_query', label: 'API Key (URL param)', hint: 'Token appended as ?api_key=<token>' },
  { value: 'none', label: 'No Auth', hint: 'No credentials sent (use only for open endpoints)' },
];

interface PartnerDetail {
  id: string;
  name: string;
  adapter_type: string;
  backend_url: string;
  auth_type: string;
  auth_username: string | null;
  auth_password: string | null;
  auth_token: string | null;
  auth_header_name: string | null;
  auth_query_param_name: string | null;
  has_auth_password?: boolean;
  has_auth_token?: boolean;
  enabled: boolean;
  repeat_alerts_enabled?: boolean;
  repeat_cadence_seconds?: Record<string, number>;
  image_display_config?: Record<string, any>;
  created_at: string;
  updated_at: string;
  tenants: Array<{ id: number; external_id: string; name: string; source_system: string; enabled: boolean; created_at: string }>;
  filters: Array<{ id: number; filter_type: string; filter_value: any; enabled: boolean }>;
  users?: Array<{ id: number; username: string; role: string; created_at: string }>;
}

interface Tenant { id: number; name: string; source_system: string; external_id: string; }

interface FilterFormData {
  filter_type: string;
  filter_value: string;
}

const FILTER_TYPES = [
  { value: 'include_event_types', label: 'Include Event Types', placeholder: '{"types":["LEAK_IMMINENT","CHANGE_NEEDED"]}' },
  { value: 'exclude_event_types', label: 'Exclude Event Types', placeholder: '{"types":["CLIP_IDENTIFYING"]}' },
  { value: 'min_severity', label: 'Minimum Severity', placeholder: '{"severity":"medium"}' },
  { value: 'site_id_include', label: 'Include Site IDs', placeholder: '{"siteIds":["101","102"]}' },
  { value: 'exclude_repeat_events', label: 'Exclude Repeat Events', placeholder: '{"enabled":true}' },
  { value: 'image_url_config', label: 'Image URL Config', placeholder: '{"baseUrl":"https://img.example.com/render"}' },
  { value: 'image_url_override', label: 'Image URL Override', placeholder: '{"baseUrl":"https://img.example.com/render"}' },
];

const emptyFilterForm: FilterFormData = { filter_type: 'include_event_types', filter_value: '' };

interface EditFormData {
  name: string;
  adapter_type: string;
  backend_url: string;
  auth_type: AuthType;
  auth_username: string;
  auth_password: string;
  auth_token: string;
  auth_header_name: string;
  auth_query_param_name: string;
  enabled: boolean;
}

export const PartnerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [partner, setPartner] = useState<PartnerDetail | null>(null);
  const [allTenants, setAllTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter form
  const [showFilterForm, setShowFilterForm] = useState(false);
  const [filterForm, setFilterForm] = useState<FilterFormData>(emptyFilterForm);
  const [filterError, setFilterError] = useState<string | null>(null);

  // Edit form
  const [showEditForm, setShowEditForm] = useState(false);
  const [editForm, setEditForm] = useState<EditFormData | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Repeat alerts (independent save so operators can tune cadence without
  // re-entering credentials).
  const [cadenceValue, setCadenceValue] = useState<RepeatCadenceValue>({ enabled: true, cadenceSeconds: {} });
  const [cadenceSaving, setCadenceSaving] = useState(false);
  const [cadenceMessage, setCadenceMessage] = useState<string | null>(null);
  const [cadenceError, setCadenceError] = useState<string | null>(null);

  // Image display config (same independent-save pattern as cadence).
  const [imageDisplayValue, setImageDisplayValue] = useState<ImageDisplayValue>({ overrides: {} });
  const [imageDisplaySaving, setImageDisplaySaving] = useState(false);
  const [imageDisplayMessage, setImageDisplayMessage] = useState<string | null>(null);
  const [imageDisplayError, setImageDisplayError] = useState<string | null>(null);

  const loadPartner = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [pResp, tResp] = await Promise.all([
        apiCall(`/api/config/partners/${id}`),
        apiCall('/api/config/tenants'),
      ]);
      if (pResp.ok) {
        const p = await pResp.json();
        setPartner(p);
        setCadenceValue({
          enabled: p.repeat_alerts_enabled === false ? false : true,
          cadenceSeconds: p.repeat_cadence_seconds || {},
        });
        setImageDisplayValue(deserializeImageDisplay(p.image_display_config, { tenantMode: false }));
        setError(null);
      } else {
        const d = await pResp.json();
        setError(d.error || 'Partner not found');
      }
      if (tResp.ok) {
        const d = await tResp.json();
        setAllTenants(d.tenants || []);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadPartner(); }, [loadPartner]);

  const assignTenant = async (tenantId: number) => {
    if (!id) return;
    await apiCall(`/api/config/partners/${id}/tenants`, {
      method: 'POST', body: JSON.stringify({ tenant_id: tenantId }),
    });
    loadPartner();
  };

  const removeTenant = async (tenantId: number) => {
    if (!id) return;
    await apiCall(`/api/config/partners/${id}/tenants/${tenantId}`, { method: 'DELETE' });
    loadPartner();
  };

  const addFilter = async () => {
    if (!id) return;
    setFilterError(null);
    let parsed: any;
    try {
      parsed = JSON.parse(filterForm.filter_value);
    } catch {
      setFilterError('Invalid JSON in filter value');
      return;
    }
    const resp = await apiCall(`/api/config/partners/${id}/filters`, {
      method: 'POST',
      body: JSON.stringify({ filter_type: filterForm.filter_type, filter_value: parsed }),
    });
    if (!resp.ok) {
      const d = await resp.json();
      setFilterError(d.error || 'Failed to add filter');
      return;
    }
    setShowFilterForm(false);
    setFilterForm(emptyFilterForm);
    loadPartner();
  };

  const deleteFilter = async (filterId: number) => {
    if (!id) return;
    if (!window.confirm('Delete this filter?')) return;
    await apiCall(`/api/config/partners/${id}/filters/${filterId}`, { method: 'DELETE' });
    loadPartner();
  };

  const openEditForm = () => {
    if (!partner) return;
    setEditForm({
      name: partner.name,
      adapter_type: partner.adapter_type,
      backend_url: partner.backend_url,
      auth_type: (partner.auth_type as AuthType) || 'basic',
      auth_username: partner.auth_username || '',
      auth_password: '',
      auth_token: '',
      auth_header_name: partner.auth_header_name || 'X-API-Key',
      auth_query_param_name: partner.auth_query_param_name || 'api_key',
      enabled: partner.enabled,
    });
    setEditError(null);
    setShowEditForm(true);
  };

  const handleEditSave = async () => {
    if (!id || !editForm) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const body: any = {
        name: editForm.name,
        adapter_type: editForm.adapter_type,
        backend_url: editForm.backend_url,
        auth_type: editForm.auth_type,
        enabled: editForm.enabled,
      };

      switch (editForm.auth_type) {
        case 'basic':
          body.auth_username = editForm.auth_username;
          if (editForm.auth_password) body.auth_password = editForm.auth_password;
          break;
        case 'bearer':
          if (editForm.auth_token) body.auth_token = editForm.auth_token;
          break;
        case 'api_key_header':
          body.auth_header_name = editForm.auth_header_name;
          if (editForm.auth_token) body.auth_token = editForm.auth_token;
          break;
        case 'api_key_query':
          body.auth_query_param_name = editForm.auth_query_param_name;
          if (editForm.auth_token) body.auth_token = editForm.auth_token;
          break;
        case 'none':
          break;
      }

      const resp = await apiCall(`/api/config/partners/${id}`, {
        method: 'PUT', body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const d = await resp.json();
        throw new Error(d.error || 'Failed to update');
      }
      setShowEditForm(false);
      loadPartner();
    } catch (err: any) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleCadenceSave = async () => {
    if (!id) return;
    setCadenceSaving(true);
    setCadenceMessage(null);
    setCadenceError(null);
    try {
      const resp = await apiCall(`/api/config/partners/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          repeat_alerts_enabled: cadenceValue.enabled !== false,
          repeat_cadence_seconds: cadenceValue.cadenceSeconds,
        }),
      });
      if (!resp.ok) {
        const d = await resp.json();
        throw new Error(d.error || 'Failed to save repeat alert settings');
      }
      setCadenceMessage('Repeat alert settings saved. Poller picks up changes within 1-2 minutes.');
      loadPartner();
    } catch (err: any) {
      setCadenceError(err.message);
    } finally {
      setCadenceSaving(false);
    }
  };

  const handleImageDisplaySave = async () => {
    if (!id) return;
    setImageDisplaySaving(true);
    setImageDisplayMessage(null);
    setImageDisplayError(null);
    try {
      const resp = await apiCall(`/api/config/partners/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          image_display_config: serializeImageDisplayForPartner(imageDisplayValue),
        }),
      });
      if (!resp.ok) {
        const d = await resp.json();
        throw new Error(d.error || 'Failed to save image display settings');
      }
      setImageDisplayMessage('Image display settings saved. Poller picks up changes within 1-2 minutes.');
      loadPartner();
    } catch (err: any) {
      setImageDisplayError(err.message);
    } finally {
      setImageDisplaySaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm(`Delete partner "${id}" and all its tenant assignments?`)) return;
    await apiCall(`/api/config/partners/${id}`, { method: 'DELETE' });
    navigate('/configuration/partners');
  };

  const assignedIds = new Set(partner?.tenants.map(t => t.id) || []);
  const unassigned = allTenants.filter(t => !assignedIds.has(t.id));

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">
        Loading partner details...
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
          <p className="text-red-600 font-medium">{error || 'Partner not found'}</p>
          <button
            onClick={() => navigate('/configuration/partners')}
            className="mt-4 px-4 py-2 bg-gray-100 rounded-lg text-sm text-gray-700 hover:bg-gray-200"
          >
            ← Back to Partners
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={() => navigate('/configuration/partners')}
              className="text-gray-400 hover:text-gray-600 text-sm"
            >
              ← Partners
            </button>
            <span className="text-gray-300">/</span>
            <span className={cn(
              'text-xs font-medium px-2 py-0.5 rounded',
              partner.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
            )}>
              {partner.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{partner.name}</h1>
          <p className="text-sm text-gray-500 mt-1">
            <span className="font-mono">{partner.id}</span>
            {' · '}
            Adapter: <span className="font-mono">{partner.adapter_type}</span>
            {' · '}
            {partner.backend_url}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openEditForm}
            className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Edit Form */}
      {showEditForm && editForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold">Edit Partner</h2>
          {editError && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{editError}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" value={editForm.name} onChange={e => setEditForm(f => f ? { ...f, name: e.target.value } : f)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Backend URL</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" value={editForm.backend_url} onChange={e => setEditForm(f => f ? { ...f, backend_url: e.target.value } : f)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Adapter Type</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={editForm.adapter_type} onChange={e => setEditForm(f => f ? { ...f, adapter_type: e.target.value } : f)}>
                <option value="generic">Generic</option>
                <option value="partner">PARTNER</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Authentication</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={editForm.auth_type}
                onChange={e => setEditForm(f => f ? { ...f, auth_type: e.target.value as AuthType } : f)}
              >
                {AUTH_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                {AUTH_TYPE_OPTIONS.find(o => o.value === editForm.auth_type)?.hint}
              </p>
            </div>

            {editForm.auth_type === 'basic' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Username</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm" value={editForm.auth_username} onChange={e => setEditForm(f => f ? { ...f, auth_username: e.target.value } : f)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Password <span className="text-gray-400">(leave blank to keep current)</span>
                    {partner.has_auth_password && <span className="ml-2 text-emerald-600">✓ stored</span>}
                  </label>
                  <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={editForm.auth_password} onChange={e => setEditForm(f => f ? { ...f, auth_password: e.target.value } : f)} />
                </div>
              </>
            )}

            {editForm.auth_type === 'bearer' && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Bearer Token <span className="text-gray-400">(leave blank to keep current)</span>
                  {partner.has_auth_token && <span className="ml-2 text-emerald-600">✓ stored</span>}
                </label>
                <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm font-mono" value={editForm.auth_token} onChange={e => setEditForm(f => f ? { ...f, auth_token: e.target.value } : f)} />
              </div>
            )}

            {editForm.auth_type === 'api_key_header' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Header Name</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm font-mono" placeholder="X-API-Key" value={editForm.auth_header_name} onChange={e => setEditForm(f => f ? { ...f, auth_header_name: e.target.value } : f)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Token <span className="text-gray-400">(leave blank to keep current)</span>
                    {partner.has_auth_token && <span className="ml-2 text-emerald-600">✓ stored</span>}
                  </label>
                  <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm font-mono" value={editForm.auth_token} onChange={e => setEditForm(f => f ? { ...f, auth_token: e.target.value } : f)} />
                </div>
              </>
            )}

            {editForm.auth_type === 'api_key_query' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Query Parameter Name</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm font-mono" placeholder="api_key" value={editForm.auth_query_param_name} onChange={e => setEditForm(f => f ? { ...f, auth_query_param_name: e.target.value } : f)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Token <span className="text-gray-400">(leave blank to keep current)</span>
                    {partner.has_auth_token && <span className="ml-2 text-emerald-600">✓ stored</span>}
                  </label>
                  <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm font-mono" value={editForm.auth_token} onChange={e => setEditForm(f => f ? { ...f, auth_token: e.target.value } : f)} />
                </div>
              </>
            )}

            <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
              <input type="checkbox" checked={editForm.enabled} onChange={e => setEditForm(f => f ? { ...f, enabled: e.target.checked } : f)} />
              Enabled
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={handleEditSave} disabled={editSaving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {editSaving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowEditForm(false)} className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Repeat Alerts */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Repeat Alerts</h2>
            <p className="text-sm text-gray-500 mt-1">
              How often the poller re-fires the same alert for the same client while the underlying state persists.
              These values are the partner default — individual tenants can override them on the Tenants page.
            </p>
          </div>
        </div>
        {cadenceError && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{cadenceError}</div>}
        {cadenceMessage && <div className="text-sm text-emerald-700 bg-emerald-50 px-3 py-2 rounded">{cadenceMessage}</div>}
        <RepeatCadenceEditor value={cadenceValue} onChange={setCadenceValue} />
        <div className="flex gap-3 pt-2">
          <button
            onClick={handleCadenceSave}
            disabled={cadenceSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {cadenceSaving ? 'Saving...' : 'Save Repeat Alert Settings'}
          </button>
        </div>
      </div>

      {/* Image Display Configuration */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Image Display Configuration</h2>
            <p className="text-sm text-gray-500 mt-1">
              Controls which visual elements the diaper-status image includes for events sent to this partner.
              These are the partner defaults — individual tenants can override on the Tenants page. Wetness % is
              off by default; everything else is on.
            </p>
          </div>
        </div>
        {imageDisplayError && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{imageDisplayError}</div>
        )}
        {imageDisplayMessage && (
          <div className="text-sm text-emerald-700 bg-emerald-50 px-3 py-2 rounded">{imageDisplayMessage}</div>
        )}
        <ImageDisplayEditor value={imageDisplayValue} onChange={setImageDisplayValue} />
        <div className="flex gap-3 pt-2">
          <button
            onClick={handleImageDisplaySave}
            disabled={imageDisplaySaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {imageDisplaySaving ? 'Saving...' : 'Save Image Display Settings'}
          </button>
        </div>
      </div>

      <HeartbeatsEditor partnerId={partner.id} />

      {/* Tenant Assignments */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Tenant Assignments
          <span className="ml-2 text-sm font-normal text-gray-400">
            ({partner.tenants.length} assigned)
          </span>
        </h2>

        {partner.tenants.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Assigned Tenants</h3>
            <div className="space-y-2">
              {partner.tenants.map(t => (
                <div key={t.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                  <div>
                    <span className="font-medium text-gray-900">{t.name}</span>
                    <span className="ml-2 text-xs text-gray-400">{t.source_system}:{t.external_id}</span>
                  </div>
                  <button onClick={() => removeTenant(t.id)} className="text-red-600 hover:text-red-800 text-xs font-medium">Remove</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {unassigned.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-600 mb-2">Available Tenants</h3>
            <div className="space-y-2">
              {unassigned.map(t => (
                <div key={t.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                  <div>
                    <span className="text-gray-700">{t.name}</span>
                    <span className="ml-2 text-xs text-gray-400">{t.source_system}:{t.external_id}</span>
                  </div>
                  <button onClick={() => assignTenant(t.id)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Assign</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {unassigned.length === 0 && partner.tenants.length === 0 && (
          <p className="text-sm text-gray-400">No tenants available. They appear when the poller discovers organizations.</p>
        )}
      </div>

      {/* Users */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Partner Users
          <span className="ml-2 text-sm font-normal text-gray-400">
            ({partner.users?.length || 0})
          </span>
        </h2>
        {partner.users && partner.users.length > 0 ? (
          <div className="space-y-2">
            {partner.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                <div>
                  <span className="font-medium text-gray-900">{u.username}</span>
                  <span className="ml-2 text-xs text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">{u.role}</span>
                </div>
                <span className="text-xs text-gray-400">
                  Created {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">
            No partner users assigned. Create one under <span className="font-mono">/users</span> with role
            <span className="font-mono"> partner</span> and select this partner.
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Filters
            <span className="ml-2 text-sm font-normal text-gray-400">
              ({partner.filters.length} active)
            </span>
          </h2>
          <button
            onClick={() => { setShowFilterForm(true); setFilterForm(emptyFilterForm); setFilterError(null); }}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
          >
            Add Filter
          </button>
        </div>

        {showFilterForm && (
          <div className="mb-4 bg-gray-50 rounded-lg p-4 space-y-3">
            {filterError && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{filterError}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Filter Type</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={filterForm.filter_type}
                  onChange={e => setFilterForm(f => ({ ...f, filter_type: e.target.value }))}
                >
                  {FILTER_TYPES.map(ft => (
                    <option key={ft.value} value={ft.value}>{ft.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Value (JSON)</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  value={filterForm.filter_value}
                  onChange={e => setFilterForm(f => ({ ...f, filter_value: e.target.value }))}
                  placeholder={FILTER_TYPES.find(ft => ft.value === filterForm.filter_type)?.placeholder}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={addFilter} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">Save Filter</button>
              <button onClick={() => setShowFilterForm(false)} className="px-3 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        )}

        {partner.filters.length > 0 ? (
          <div className="space-y-2">
            {partner.filters.map(f => (
              <div key={f.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-sm text-gray-700">{f.filter_type}</span>
                  <span className="ml-2 text-xs text-gray-400 truncate">{JSON.stringify(f.filter_value)}</span>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <span className={cn('text-xs font-medium px-2 py-0.5 rounded', f.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500')}>
                    {f.enabled ? 'Active' : 'Inactive'}
                  </span>
                  <button onClick={() => deleteFilter(f.id)} className="text-red-600 hover:text-red-800 text-xs font-medium">Delete</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !showFilterForm && <p className="text-sm text-gray-400">No filters — all events will be forwarded to this partner.</p>
        )}
      </div>
    </div>
  );
};
